import React from 'react';

import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import {
  canJobBeModified,
  createDataCyForJob,
  hasJobBeenModified,
  isShiftAtCapacity,
  type TableDataRow,
  type TableDataShift,
} from 'src/utils/jmrUtils';
import { areConflictsResolved, getJobError } from 'src/utils/placementSelectionUtils';

import ConflictPopover from './conflictPopover';
import ModifyBtn from './modifyBtn';

interface ExpandableMultiShiftJobRowProps {
  row: TableDataRow;
  selectedJobRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  jobsExceedingRestrictionsInAggregate: Set<string>;
  exceededRestrictionIds: Set<number>;
  onClickModify: (row: TableDataRow, shiftId?: string) => void;
  rowComponents?: React.ComponentType[];
}

const ExpandableMultiShiftJobRow = ({
  row,
  onClickModify,
  selectedJobRefs,
  jobsExceedingRestrictionsInAggregate,
  exceededRestrictionIds,
  rowComponents,
}: ExpandableMultiShiftJobRowProps) => {
  const { jmrResults, backgroundFormData } = useAppSelector((state) => state.newJmrPage);
  const dispatch = useAppDispatch();
  const isT2Experience = useIsT2Experience();

  const toggleShift = (rowId: string, shiftId: string) => {
    const newResults = jmrResults.map((row) => {
      if (row.id !== rowId) return row;
      const newShifts = row.shifts.map((s) =>
        s.id === shiftId ? { ...s, isChecked: !s.isChecked, totalHours: s.isChecked ? '' : s.totalHours } : s,
      );
      const anyChecked = newShifts.some((s) => s.isChecked);
      const totalHours = newShifts.reduce((sum, s) => (s.isChecked ? sum + (+s.totalHours || 0) : sum), 0).toString();
      return { ...row, shifts: newShifts, isChecked: anyChecked, totalHours: anyChecked ? totalHours : '' };
    });
    dispatch(setJmrResults(newResults));
  };

  const setShiftTotalHours = (rowId: string, shiftId: string, value: string) => {
    const newResults = jmrResults.map((row) => {
      if (row.id !== rowId) return row;
      const newShifts = row.shifts.map((s) => (s.id === shiftId ? { ...s, totalHours: value } : s));
      const totalHours = newShifts.reduce((sum, s) => (s.isChecked ? sum + (+s.totalHours || 0) : sum), 0).toString();
      return { ...row, shifts: newShifts, totalHours };
    });
    dispatch(setJmrResults(newResults));
  };

  /** A shift rendered as a job row, so shift-level data can reuse the job-level components */
  const toShiftView = (shift: TableDataShift) => ({
    ...row,
    shifts: [],
    totalHours: shift.totalHours,
    jobCapacity: shift.jobCapacity,
    conflicts: shift.conflicts,
    maximumHoursAssociateCanDo: shift.maximumHoursAssociateCanDo,
    modificationNotes: shift.modificationNotes,
  });

  const isShiftModified = (shift: TableDataShift) =>
    isT2Experience ? !!shift.modificationNotes?.trim().length : hasJobBeenModified(shift.conflicts);

  // later modified shifts can diverge and are deliberately ignored
  const firstModifiedShift = row.shifts.find(isShiftModified);
  const hasChildBeenModified = !!firstModifiedShift;
  // the parent's own conflicts never carry modifications, so it borrows the modified shift's
  const parentView = firstModifiedShift ? { ...toShiftView(firstModifiedShift), shifts: row.shifts } : row;

  return (
    <UncontrolledExpandableRow key={row.id} rowComponents={rowComponents}>
      <TableCell>
        <Column spacing="200">
          <Text type="b300">{row.name}</Text>
          <Text type="b200" color="secondary">
            {row.jobType}
          </Text>
        </Column>
      </TableCell>
      <TableCell>
        <Text type="b200" color="secondary">
          {row.shifts.length} shifts
        </Text>
      </TableCell>
      <TableCell>
        <Column spacing="200">
          <ConflictPopover jobMatch={parentView} isModified={hasChildBeenModified} {...{ exceededRestrictionIds }} />
          {!hasChildBeenModified && !areConflictsResolved(row.conflicts) && (
            <Text type="b200" color="secondary">
              {`Maximum hrs: ${row.maximumHoursAssociateCanDo}`}
            </Text>
          )}
        </Column>
      </TableCell>
      <TableCell />
      <TableCell>
        <Text type="b200" color="secondary">
          {row.shifts.filter((s) => s.isChecked).length} selected
        </Text>
      </TableCell>
      <TableCell width={80} />
      {row.shifts.map((shift) => {
        const shiftView = toShiftView(shift);

        return (
          <TableRow key={shift.id} backgroundColor="secondary">
            <TableCell />
            <TableCell>
              <Column spacing="200">
                <Text type="b300">{shift.shiftCode}</Text>
                <Text type="b200" color="secondary">
                  {`${shift.jobCapacity || 0} hrs capacity`}
                </Text>
              </Column>
            </TableCell>
            <TableCell>
              <Column spacing="200">
                <ConflictPopover jobMatch={shiftView} shiftCode={shift.shiftCode} {...{ exceededRestrictionIds }} />
                <Text type="b200" color="secondary">
                  {areConflictsResolved(shift.conflicts)
                    ? 'Full Shift'
                    : `Maximum hrs: ${shift.maximumHoursAssociateCanDo}`}
                </Text>
              </Column>
            </TableCell>
            <TableCell>
              <ModifyBtn
                row={shiftView}
                shiftId={shift.id}
                shiftCode={shift.shiftCode}
                hasBeenModified={isShiftModified(shift)}
                canBeModified={
                  isT2Experience ? row.tierTwoPlusJobModified : canJobBeModified(shift.conflicts, [], shift.jobCapacity)
                }
                onClickModify={onClickModify}
              />
            </TableCell>
            <TableCell>
              {shift.maximumHoursAssociateCanDo === 0 || isShiftAtCapacity(shift) ? (
                <Tooltip
                  title={
                    isShiftAtCapacity(shift)
                      ? 'Capacity is full.'
                      : 'Apply job modifications to enable selection of this job.'
                  }
                  position="top"
                >
                  <Checkbox disabled={true} />
                </Tooltip>
              ) : (
                <Checkbox
                  disabled={
                    (!shift.isChecked && isT2Experience && jmrResults.some((r) => r.isChecked)) ||
                    (isT2Experience && row.tierTwoPlusJobModified && !shift.modificationNotes?.trim().length)
                  }
                  checked={shift.isChecked}
                  onChange={() => toggleShift(row.id, shift.id)}
                  data-cy={`${createDataCyForJob(row.name)}-${shift.shiftCode}-checkbox`}
                  aria-label={`${row.name} ${shift.shiftCode} Checkbox`}
                />
              )}
            </TableCell>
            <TableCell width={80} alignmentVertical="top">
              <div className="jobMatchesTable__input-wrapper">
                {shift.isChecked ? (
                  <Input
                    ref={(ref) =>
                      ref ? selectedJobRefs.current.set(shift.id, ref) : selectedJobRefs.current.delete(shift.id)
                    }
                    type="text"
                    value={shift.totalHours}
                    onChange={(value: string) => setShiftTotalHours(row.id, shift.id, value)}
                    error={
                      !!getJobError(shiftView, jmrResults, backgroundFormData, jobsExceedingRestrictionsInAggregate)
                    }
                    errorMessage={getJobError(
                      shiftView,
                      jmrResults,
                      backgroundFormData,
                      jobsExceedingRestrictionsInAggregate,
                    )}
                    size="small"
                    placeholder="hrs..."
                    data-cy={`${createDataCyForJob(row.name)}-${shift.shiftCode}-totalHours`}
                  />
                ) : (
                  ''
                )}
              </div>
            </TableCell>
          </TableRow>
        );
      })}
    </UncontrolledExpandableRow>
  );
};

export default ExpandableMultiShiftJobRow;
